import React from "react";
import { Link } from "react-router-dom";
import { Building2, Compass, FileText, LandPlot, ArrowRight, CheckCircle2 } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import SEO from "@/components/SEO";

const CATEGORIES = [
  {
    icon: Building2,
    title: "Real Estate Services",
    to: "/properties",
    items: [
      "Residential Property Buy & Sell",
      "Land & Plot Buy / Sell Assistance",
      "Flat & Apartment Consultancy",
      "Commercial Property Consultancy",
      "Investment Property Assistance",
      "Agricultural Land Transaction Assistance",
      "Property Site Visits",
      "Buyer–Seller Coordination",
      "Property Listing & Marketing",
      "Price Negotiation Assistance",
      "Documentation Coordination",
      "Due Diligence Coordination",
    ],
  },
  {
    icon: Compass,
    title: "Land Survey & Technical",
    to: "/land-survey",
    items: [
      "RTK GNSS Digital Land Survey",
      "DGPS Land Survey",
      "Total Station Survey",
      "Boundary & Demarcation Survey",
      "Plot Measurement",
      "Agricultural Land Survey",
      "Large Plot Mapping",
      "Land Area Calculation",
      "Site Plan Preparation",
      "AutoCAD Survey Drafting",
      "Contour Survey (subject to capability)",
      "Land Subdivision Planning",
      "Field Book Preparation",
      "Survey Report Preparation",
    ],
  },
  {
    icon: FileText,
    title: "Documentation & Revenue",
    to: "/documentation",
    items: [
      "Land Mutation Assistance",
      "Record of Rights (ROR / Porcha) Assistance",
      "LR & RS Record Coordination",
      "Land Conversion Application Assistance",
      "Deed Registration Assistance",
      "Certified Copy Application Assistance",
      "Mouza Map Assistance",
      "Plot & Khatian Verification Coordination",
      "Land Classification Information",
      "Boundary Dispute Documentation Support",
      "Land Revenue Documentation",
      "Application Preparation",
      "Revenue Office Process Guidance",
    ],
  },
  {
    icon: LandPlot,
    title: "Property Planning",
    to: "/planning",
    items: [
      "Plot Planning",
      "Site Layout Planning",
      "Residential Site Planning",
      "Floor Plan Drafting",
      "Preliminary Building Layout",
      "Land Subdivision Concept",
      "Development Feasibility Coordination",
      "Agricultural Plot Mapping",
      "Property Measurement & Layout",
    ],
  },
];

export default function Services() {
  return (
    <>
      <SEO
        title="Our Services — Real Estate, Land Survey, Documentation & Planning"
        description="Four core service pillars: property buy & sell, RTK GNSS / DGPS / Total Station surveys, land documentation (mutation, porcha, conversion) and property planning."
        path="/services"
        keywords="real estate services Kolkata, land survey services, documentation services West Bengal, property planning"
      />
      <section className="bg-[#101827] text-white">
        <div className="ae-container pt-28 pb-16">
          <div className="ae-eyebrow mb-4">Our Services</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] max-w-4xl">
            A full-spectrum property services catalogue
          </h1>
          <p className="text-white/75 mt-5 max-w-3xl leading-relaxed">
            Four core pillars — real estate advisory, digital land surveying, documentation support
            and property planning — each with clear scope and client-first execution.
          </p>
        </div>
      </section>

      <section className="ae-section">
        <div className="ae-container space-y-16">
          {CATEGORIES.map((c, i) => (
            <div key={c.title} className="grid lg:grid-cols-12 gap-10 items-start" data-testid={`service-cat-${i}`}>
              <div className="lg:col-span-4">
                <div className="sticky top-28">
                  <div className="w-14 h-14 rounded-xl bg-[#101827] text-[#C5A46D] flex items-center justify-center mb-5">
                    <c.icon className="w-6 h-6" />
                  </div>
                  <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#101827] leading-tight">{c.title}</h2>
                  <Link to={c.to} className="ae-btn-primary mt-5">Explore {c.title.split(" ")[0]} <ArrowRight className="w-4 h-4" /></Link>
                </div>
              </div>
              <div className="lg:col-span-8">
                <div className="grid sm:grid-cols-2 gap-3">
                  {c.items.map((it) => (
                    <div key={it} className="ae-card p-4 flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#059669] shrink-0 mt-0.5" />
                      <div className="text-sm font-medium text-[#1F2937]">{it}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="ae-section bg-[#F4F6F8]">
        <div className="ae-container">
          <div className="ae-card p-8 md:p-12 border-l-4 border-l-[#C5A46D]">
            <div className="ae-eyebrow mb-3">Compliance Note</div>
            <h3 className="font-display text-xl md:text-2xl font-semibold text-[#101827] mb-3">We work within a lawful consultancy scope.</h3>
            <p className="text-[#475569] leading-relaxed max-w-3xl">
              Apurba Estate provides consultancy, administrative assistance, documentation coordination
              and technical services. We are not a government department, registration authority, advocate
              or statutory approval body. Legal opinions, court representation and statutory approvals
              remain with appropriately qualified or authorised professionals.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
